import React, {useState, useRef, useEffect, useMemo} from 'react';
import {Icon} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import GooglePicker from '../../../forecast-app/shared/components/google-picker/google_picker_new';
import type {AttachedFile, ChatMessage, Phase} from './types';
import {useChatAttachments} from './useChatAttachments';
import {TOOL_LABELS, SUGGESTIONS} from './constants';
import {
	LeftPanel,
	RightChatPanel,
	ChatPanelHeader,
	NovaAvatar,
	ChatPanelHeaderText,
	ChatPanelTitle,
	ChatPanelSubtitle,
	PanelLabel,
	MessagesArea,
	UserBubble,
	AssistantBubble,
	QuickReplyRow,
	QuickReplyBtn,
	Cursor,
	ToolChipsRow,
	ToolChip,
	ChipCount,
	ChipDot,
	InputArea,
	InputBox,
	ChatTextarea,
	InputFooter,
	SendButton,
	StopButton,
	AttachmentRow,
	AttachmentChip,
	ChipFilename,
	ChipRemove,
	AttachBtn,
	AttachDropdown,
	AttachOption,
	AttachError,
	TypingBubble,
	TypingDot,
	WelcomeArea,
	WelcomeTitle,
	WelcomeSubtitle,
	SuggestionLabel,
	SuggestionCard,
} from './styles';

interface ChatPanelProps {
	messages: ChatMessage[];
	phase: Phase;
	onSend: (text: string, attachment: AttachedFile | null) => void;
	onStop: () => void;
	toolStatuses: Record<string, boolean>;
	side?: 'left' | 'right';
}

const ChatPanel: React.FC<ChatPanelProps> = ({messages, phase, onSend, onStop, toolStatuses, side}) => {
	const {formatMessage} = useIntl();
	const [inputValue, setInputValue] = useState('');
	const [focused, setFocused] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const {
		attachedFile,
		clearAttachment,
		attachDropdownOpen,
		setAttachDropdownOpen,
		attachLoading,
		attachError,
		fileInputRef,
		attachBtnRef,
		handleLocalFile,
		handleDrivePick,
	} = useChatAttachments();

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
	}, [messages]);

	const canSend = phase === 'idle' || phase === 'awaiting_approval';

	const handleSendClick = () => {
		const text = inputValue.trim();
		if (!text && !attachedFile) return;
		if (!canSend) return;
		setInputValue('');
		onSend(text, attachedFile);
		clearAttachment();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendClick();
		}
	};

	const handleSuggestion = (text: string) => {
		if (!canSend) return;
		onSend(text, null);
	};

	const isRunning = phase === 'running' || phase === 'committing';
	const showWelcome = messages.length === 0 && phase === 'idle';
	const placeholder =
		side === 'right' || phase === 'awaiting_approval'
			? formatMessage({id: 'ai_project_planning.placeholder_refine'})
			: formatMessage({id: 'ai_project_planning.placeholder_idle'});

	// Only the most-recent unanswered question shows quick reply buttons.
	// Showing all past options lets users accidentally re-submit stale answers.
	// useMemo avoids re-scanning on unrelated re-renders (focus, input changes, etc.).
	const lastQuestionMsgId = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].questionOptions?.length) return messages[i].id;
		}
		return undefined;
	}, [messages]);

	// Group consecutive tool messages into a single chips row
	const renderedMessages: React.ReactNode[] = [];
	let i = 0;
	while (i < messages.length) {
		const msg = messages[i];
		if (msg.role === 'tool') {
			const toolGroup: ChatMessage[] = [];
			while (i < messages.length && messages[i].role === 'tool') {
				toolGroup.push(messages[i]);
				i++;
			}
			renderedMessages.push(
				<ToolChipsRow key={toolGroup[0].id}>
					{toolGroup.map(t => {
						const isDone = toolStatuses[t.id] === true;
						const labelId = TOOL_LABELS[t.toolName ?? ''];
						const label = labelId ? formatMessage({id: labelId}) : t.toolName || 'tool';
						return (
							<ToolChip key={t.id} done={isDone}>
								<ChipDot done={isDone} />
								{label}
								{(t.callCount ?? 1) > 1 && <ChipCount>× {t.callCount}</ChipCount>}
							</ToolChip>
						);
					})}
				</ToolChipsRow>
			);
		} else {
			if (msg.role === 'user') {
				renderedMessages.push(<UserBubble key={msg.id}>{msg.content}</UserBubble>);
			} else {
				renderedMessages.push(
					<AssistantBubble key={msg.id} compact={side === 'right'}>
						{msg.content}
						{msg.isStreaming && <Cursor />}
					</AssistantBubble>
				);
				// Quick reply options — only on the most-recent unanswered question
				if (msg.questionOptions && msg.questionOptions.length > 0 && canSend && msg.id === lastQuestionMsgId) {
					renderedMessages.push(
						<QuickReplyRow key={`${msg.id}-opts`}>
							{msg.questionOptions.map(opt => (
								<QuickReplyBtn
									key={opt}
									type="button"
									onClick={() => {
										onSend(opt, null);
									}}
								>
									{opt}
								</QuickReplyBtn>
							))}
						</QuickReplyRow>
					);
				}
			}
			i++;
		}
	}

	const PanelWrapper = side === 'right' ? RightChatPanel : LeftPanel;

	return (
		<PanelWrapper>
			{side === 'right' ? (
				<ChatPanelHeader>
					<NovaAvatar>✦</NovaAvatar>
					<ChatPanelHeaderText>
						<ChatPanelTitle>Nova</ChatPanelTitle>
						<ChatPanelSubtitle>{formatMessage({id: 'sow_review.nova_subtitle'})}</ChatPanelSubtitle>
					</ChatPanelHeaderText>
				</ChatPanelHeader>
			) : (
				<PanelLabel>{formatMessage({id: 'ai_project_planning.panel_label'})}</PanelLabel>
			)}
			{showWelcome ? (
				<WelcomeArea>
					<WelcomeTitle>{formatMessage({id: 'ai_project_planning.welcome_title'})}</WelcomeTitle>
					<WelcomeSubtitle>{formatMessage({id: 'ai_project_planning.welcome_subtitle'})}</WelcomeSubtitle>
					<SuggestionLabel>{formatMessage({id: 'ai_project_planning.suggestion_label'})}</SuggestionLabel>
					{SUGGESTIONS.map((s, idx) => (
						<SuggestionCard
							key={s}
							onClick={() => handleSuggestion(formatMessage({id: `ai_project_planning.suggestion_${idx + 1}`}))}
						>
							{formatMessage({id: `ai_project_planning.suggestion_${idx + 1}`})}
						</SuggestionCard>
					))}
				</WelcomeArea>
			) : (
				<MessagesArea>
					{renderedMessages}
					{isRunning && (
						<TypingBubble>
							<TypingDot delay="0s" />
							<TypingDot delay="0.2s" />
							<TypingDot delay="0.4s" />
						</TypingBubble>
					)}
					<div ref={messagesEndRef} />
				</MessagesArea>
			)}
			<InputArea focused={focused} compact={side === 'right'}>
				{side !== 'right' && (
					<>
						{attachedFile && !attachError && (
							<AttachmentRow>
								<AttachmentChip>
									<Icon icon="task" size="xs" />
									<ChipFilename title={attachedFile.filename}>{attachedFile.filename}</ChipFilename>
									<ChipRemove onClick={clearAttachment} title="Remove attachment">
										<Icon icon="closeS" size="xs" />
									</ChipRemove>
								</AttachmentChip>
							</AttachmentRow>
						)}
						{attachError && <AttachError>{attachError}</AttachError>}
					</>
				)}
				<InputBox focused={focused} disabled={!canSend}>
					<ChatTextarea
						compact={side === 'right'}
						value={inputValue}
						onChange={e => setInputValue(e.target.value)}
						onKeyDown={handleKeyDown}
						onFocus={() => setFocused(true)}
						onBlur={() => setFocused(false)}
						placeholder={placeholder}
						disabled={!canSend}
					/>
					<InputFooter>
						{side !== 'right' && (
							<>
								<div ref={attachBtnRef} style={{position: 'relative', marginRight: 'auto'}}>
									<AttachBtn
										onClick={() => !attachLoading && setAttachDropdownOpen(v => !v)}
										disabled={!canSend || attachLoading}
										title={formatMessage({id: 'ai_project_planning.attach_tooltip'})}
									>
										{attachLoading ? <Icon icon="timer" size="m" /> : <Icon icon="paperclip" size="m" />}
									</AttachBtn>
									{attachDropdownOpen && (
										<AttachDropdown>
											<AttachOption
												onClick={() => {
													setAttachDropdownOpen(false);
													fileInputRef.current?.click();
												}}
											>
												{formatMessage({id: 'ai_project_planning.upload_from_computer'})}
											</AttachOption>
											<GooglePicker
												onChange={handleDrivePick}
												mimeTypes={[
													'application/pdf',
													'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
												]}
												multiselect={false}
											>
												<AttachOption>
													{formatMessage({id: 'ai_project_planning.select_from_drive'})}
												</AttachOption>
											</GooglePicker>
										</AttachDropdown>
									)}
								</div>
								<input
									ref={fileInputRef}
									type="file"
									accept=".pdf,.docx"
									style={{display: 'none'}}
									onChange={handleLocalFile}
								/>
							</>
						)}
						{isRunning ? (
							<StopButton onClick={onStop}>{formatMessage({id: 'ai_project_planning.stop_button'})}</StopButton>
						) : (
							<SendButton
								onClick={handleSendClick}
								disabled={!canSend || (!inputValue.trim() && !attachedFile)}
								title={formatMessage({id: 'ai_project_planning.send_button'})}
							>
								<Icon icon="arrowRight" size="s" />
							</SendButton>
						)}
					</InputFooter>
				</InputBox>
			</InputArea>
		</PanelWrapper>
	);
};

export default ChatPanel;
